import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useRouter } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, { FadeIn, ZoomIn } from 'react-native-reanimated';

import { AppText } from '@/components/ui/AppText';
import { Button } from '@/components/ui/Button';
import { STORAGE_KEYS } from '@/config';
import { useThemePalette } from '@/config/theme';
import { useAuth } from '@/lib/auth/AuthProvider';

/**
 * One-time post-onboarding nudge that points a freshly-activated user at their
 * FIRST prediction. Shown once ever (guarded by STORAGE_KEYS.seenFirstPredictNudge)
 * to signed-in users when they land on the daily hub after finishing onboarding.
 * Renders nothing but the modal; dismiss or "predict" both persist the flag so it
 * never reappears. Mirrors the ChallengesPopupGate pattern + premium Volt style.
 */
export function FirstPredictNudge() {
  const { userId } = useAuth();
  const { t } = useTranslation();
  const palette = useThemePalette();
  const router = useRouter();
  const [visible, setVisible] = useState(false);
  const decided = useRef(false);

  useEffect(() => {
    if (decided.current || !userId) return;
    decided.current = true;
    AsyncStorage.getItem(STORAGE_KEYS.seenFirstPredictNudge)
      .then((seen) => {
        if (seen !== 'true') setVisible(true);
      })
      // Fail OPEN would re-show forever on a storage error; fail CLOSED instead so
      // a flaky read never traps the user behind a recurring modal.
      .catch(() => undefined);
  }, [userId]);

  const persist = () => {
    AsyncStorage.setItem(STORAGE_KEYS.seenFirstPredictNudge, 'true').catch(() => {});
    setVisible(false);
  };

  const predict = () => {
    persist();
    router.push('/games/score-predict' as never);
  };

  if (!visible) return null;
  return (
    <Modal visible transparent animationType="fade" onRequestClose={persist} statusBarTranslucent>
      <Animated.View
        entering={FadeIn.duration(180)}
        className="flex-1 items-center justify-center px-6"
        style={{ backgroundColor: 'rgba(0,0,0,0.66)' }}
      >
        <Pressable className="absolute inset-0" accessibilityRole="button" onPress={persist} />
        <Animated.View
          entering={ZoomIn.springify().damping(16)}
          className="w-full items-center rounded-3xl border border-volt-line bg-surface-2 px-5 pb-5 pt-6"
          style={{ maxWidth: 360 }}
        >
          <View className="h-16 w-16 items-center justify-center rounded-full bg-volt-glass-strong">
            <Ionicons name="football" size={30} color={palette.voltBright} />
          </View>
          <AppText variant="title" weight="bold" className="mt-3 text-center text-ink">
            {t('today.firstPredict.title')}
          </AppText>
          <AppText variant="caption" className="mt-2 text-center text-soft">
            {t('today.firstPredict.body')}
          </AppText>
          <View className="mt-5 w-full">
            <Button label={t('today.firstPredict.cta')} variant="primary" onPress={predict} />
          </View>
          <Pressable accessibilityRole="button" onPress={persist} className="mt-2 items-center py-2">
            <AppText variant="micro" className="text-muted">
              {t('today.firstPredict.later')}
            </AppText>
          </Pressable>
        </Animated.View>
      </Animated.View>
    </Modal>
  );
}
